import React, { useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import i18n from "../i18n";
import { useHideData } from "../hooks/useHideData";
import { maskForClass } from "../helpers/hideData";

// How long a revealed value stays readable before it masks itself again.
const REVEAL_MS = 30000;

// What a screen reader announces in place of a masked value.
//
// Read through i18n directly instead of useTranslation on purpose. This component is rendered by
// every money formatter — several hundred instances on the product-request calculation alone —
// and a translation hook in each would add a subscription per value and, worse, could suspend
// (the namespaces load asynchronously; see the crash documented in useHideDataShortcut.js).
// The trade-off: this string does not update on its own when the language changes. That is
// acceptable here, because switching the language re-renders the tree anyway.
const HIDDEN_LABEL = () =>
  i18n.t("Hidden by Privacy Mode", { ns: "components" });

// Renders a value that Privacy Mode should hide.
//
//   <HiddenValue dataClass="partners">{customer.name}</HiddenValue>
//   <HiddenValue dataClass="orderValues">{formatCurrencyText(total, "EUR")}</HiddenValue>
//
// When the value's data class is not being masked it renders the children untouched, with no
// wrapper element, so it is free to use anywhere. When it is, the text is replaced by
// same-length bullets and drawn with the user's chosen treatment (blur, dots or bars) — the
// layout never shifts and the value is not selectable, so it cannot be copied out of a table.
//
// `dataClass` is one of the keys in DATA_CLASSES (src/helpers/hideData.js) and decides both
// WHETHER the value is masked and HOW: money keeps its separators and currency symbol and masks
// only the digits; names, documents and free text mask every non-space character. Omitting it
// means "mask whenever the mode is on", masked as text.
//
// Clicking a masked value reveals it for REVEAL_MS, then it masks itself again.
//
// The click handler is attached ONLY while masked. That matters: a revealed value frequently
// sits inside a <Link>, and an always-on handler swallowed the click with preventDefault, so
// the link never opened. Once revealed the element is inert — ordinary text that can be
// selected, copied and clicked straight through to whatever is underneath.
//
// Revealing works because the formatters in src/helpers/Formatters.js hand this component the
// REAL value — masking happens here and nowhere else.
//
// Pass `tooltip` when the value also has a hover tooltip (usually the untruncated version). The
// tooltip is then suppressed while masked and appears again once the value is revealed:
//
//   <HiddenValue dataClass="partners" tooltip={name.length > 35 ? name : undefined}>
//     {truncated}
//   </HiddenValue>
const HiddenValue = ({ children, dataClass, tooltip, className, style }) => {
  const { isMasked, treatment } = useHideData();
  const [revealed, setRevealed] = useState(false);
  const timerRef = useRef(null);

  const masked = isMasked(dataClass);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Leaving the masked state (mode off, or the class switched off) clears the reveal, so
  // turning the mode back on never shows a value the user revealed minutes ago.
  useEffect(() => {
    if (!masked && revealed) setRevealed(false);
  }, [masked, revealed]);

  // A tooltip passed in has to survive the unmasked case too — returning early here is what
  // silently removed tooltips for everyone once a call site handed its OverlayTrigger over.
  //
  // `className` and `style` have to survive it as well. They used to be applied only on the
  // masked branch, so a call site that styled the value — MediaFileUploader's small grey
  // caption, SupplierAvatar's sizing, the truncation in ContactSuppliersModal — lost that
  // styling for everyone with the mode switched OFF, which is almost everyone. The wrapper is
  // still omitted when there is nothing to put on it, so the common call site adds no DOM.
  if (!masked) {
    const needsWrapper = className || style;
    if (!tooltip) {
      return needsWrapper ? (
        <span className={className} style={style}>
          {children}
        </span>
      ) : (
        <>{children}</>
      );
    }
    return (
      <OverlayTrigger
        placement="top"
        popperConfig={{ strategy: "fixed" }}
        overlay={<Tooltip>{tooltip}</Tooltip>}
      >
        <span className={className} style={style}>
          {children}
        </span>
      </OverlayTrigger>
    );
  }

  // Only ever called while masked. preventDefault + stopPropagation keep the first click from
  // following the surrounding link — the user wanted to read the value, not navigate.
  const handleReveal = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setRevealed(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setRevealed(false), REVEAL_MS);
  };

  // Nothing to hide when there is nothing there. Call sites routinely pass an expression that
  // can come out empty — `shipment?.carrier?.name` with no carrier, `!loading && name` while a
  // spinner runs — and masking that produced an empty treated span plus a screen reader
  // announcing a hidden value where the page had said nothing at all.
  if (children === null || children === undefined || children === false || children === "") {
    return <>{children}</>;
  }

  // Only plain text can be masked character by character. A nested element (a link, a badge)
  // still gets the treatment and the copy protection, but its real text stays in the DOM — so
  // pass strings and numbers wherever the value actually matters.
  const isMaskable =
    typeof children === "string" || typeof children === "number";
  const content =
    revealed || !isMaskable ? children : maskForClass(children, dataClass);

  const element = (
    <span
      className={[
        "hide-data-value",
        revealed ? "hide-data-revealed" : `hide-data-${treatment}`,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      onClick={revealed ? undefined : handleReveal}
      aria-hidden={revealed ? undefined : "true"}
    >
      {content}
    </span>
  );

  // A screen reader must not read out a value we are hiding — spoken in an open-plan office it
  // is the same leak as a visible one — which is why the bullets carry aria-hidden.
  //
  // But aria-hidden alone removes the value from the accessibility tree entirely, and masked
  // values often ARE the text of a link (see StripedRowsTable/TableRow.js). The link was then
  // left with no accessible name at all: a screen reader announced "link" and nothing else, and
  // focusable content under aria-hidden is a formal ARIA violation on top.
  //
  // The replacement text restores the name and says what happened, without revealing anything.
  const withScreenReaderText = revealed ? (
    element
  ) : (
    <>
      {element}
      <span className="visually-hidden">{HIDDEN_LABEL()}</span>
    </>
  );

  // A tooltip that would spell the value out has to follow the SAME reveal state as the value
  // itself — otherwise it either leaks the masked value on hover, or (the bug this fixes) stays
  // stubbornly closed after the user deliberately revealed it. Owning the OverlayTrigger here is
  // the only place that knows both.
  if (!tooltip) return withScreenReaderText;
  return (
    <>
      <OverlayTrigger
        placement="top"
        popperConfig={{ strategy: "fixed" }}
        trigger={revealed ? ["hover", "focus"] : []}
        overlay={<Tooltip>{tooltip}</Tooltip>}
      >
        {element}
      </OverlayTrigger>
      {!revealed && <span className="visually-hidden">{HIDDEN_LABEL()}</span>}
    </>
  );
};

export default HiddenValue;
